import React from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import { useStyle } from 'react-native-style-utilities';
import {
  Button,
  DisplayText,
  MobileHeader as GarnishMobileHeader,
  theme,
  useBreakpointHelpers,
} from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

import type { FooterProps, HeaderProps } from '../CreditCardForm.types';

export const LineItem = ({
  style,
  children,
}: Pick<ViewProps, 'style' | 'children'>) => {
  const { currentBreakpoint } = useBreakpointHelpers();
  const paddingHorizontal = currentBreakpoint.isMobile ? 16 : 24;
  const dynamic = useStyle(() => ({ paddingHorizontal }), [paddingHorizontal]);

  return <View style={[styles.lineItem, dynamic, style]}>{children}</View>;
};

export const Header = (props: HeaderProps) => {
  const { currentBreakpoint } = useBreakpointHelpers();
  const Responsive = currentBreakpoint.isMobile ? MobileHeader : DesktopHeader;

  return <Responsive {...props} />;
};

export const Footer = ({
  isEditing,
  isSaving,
  isDeleting,
  canSubmit,
  onDelete,
  onSubmit,
}: FooterProps) => {
  const { t } = useLocalizationContext();
  const paddingHorizontal = useHorizontalPadding(isEditing);
  const style = useStyle(() => ({ paddingHorizontal }), [paddingHorizontal]);

  return (
    <View style={[styles.row, style]}>
      {isEditing ? (
        <Button
          testID="account.credit-card-form.remove"
          size="large"
          palette="caution"
          width="48.5%"
          isLoading={isDeleting}
          accessibilityRole="button"
          accessibilityLabel={t('account.credit-card-form.remove')}
          accessibilityHint={t('account.credit-card-form.remove')}
          onPress={onDelete}
        >
          {t('account.credit-card-form.remove')}
        </Button>
      ) : null}
      <Button
        testID="account.credit-card-form.submit"
        size="large"
        palette="primary"
        width={isEditing ? '48.5%' : '100%'}
        isLoading={isSaving}
        disabled={!canSubmit}
        accessibilityRole="button"
        accessibilityLabel={t('account.credit-card-form.save')}
        accessibilityHint={t('account.credit-card-form.save')}
        accessibilityState={{ disabled: !canSubmit }}
        onPress={onSubmit}
      >
        {t('account.credit-card-form.save')}
      </Button>
    </View>
  );
};

const DesktopHeader = ({ isEditing }: HeaderProps) => {
  const { t } = useLocalizationContext();
  const title = t(`account.credit-card-form.${isEditing ? 'edit' : 'add'}`);

  return (
    <View style={styles.desktopHeader}>
      <DisplayText size={3}>{title}</DisplayText>
    </View>
  );
};

const MobileHeader = ({ isEditing, subtitle }: HeaderProps) => {
  const { t } = useLocalizationContext();
  const title = t(`account.credit-card-form.${isEditing ? 'edit' : 'add'}`);

  return (
    <View style={styles.mobileHeaderPadding}>
      <GarnishMobileHeader title={title} subtitle={subtitle} />
    </View>
  );
};

const useHorizontalPadding = (isEditing?: boolean): number => {
  const { currentBreakpoint } = useBreakpointHelpers();

  if (currentBreakpoint.isMobile) return theme.spacing.medium;

  if (isEditing) return theme.spacing.xlarge;

  return theme.spacing.xxlarge;
};

const styles = StyleSheet.create({
  desktopHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing.large,
  },
  mobileHeaderPadding: {
    paddingHorizontal: theme.spacing.medium,
  },
  lineItem: {
    marginBottom: theme.spacing.large,
  },
  row: {
    paddingVertical: theme.spacing.large,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
});
